import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Newspaper, ExternalLink, Pin, Link2, ArrowRight } from "lucide-react";
import { api, formatApiError } from "../context/AuthContext";
import { toast } from "sonner";

const CATS = [
  { key: "sst", label: "SST", color: "text-emerald-400", dot: "bg-emerald-500", badge: "border-emerald-700 bg-emerald-950 text-emerald-400" },
  { key: "ssiap", label: "SSIAP", color: "text-red-400", dot: "bg-red-500", badge: "border-red-700 bg-red-950 text-red-400" },
  { key: "carte_pro", label: "Cartes Pro", color: "text-orange-400", dot: "bg-orange-500", badge: "border-orange-700 bg-orange-950 text-orange-400" },
];

export default function ActusPanel({ compact = false }) {
  const [data, setData] = useState(null);
  const [cat, setCat] = useState("sst");

  useEffect(() => {
    api.get("/actualites").then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, []);

  if (!data) return null;
  const annonces = (compact ? data.annonces.filter((a) => a.epingle) : data.annonces).slice(0, compact ? 3 : 50);
  const actus = (data.actus?.[cat] || []).slice(0, compact ? 2 : 10);
  const liens = data.liens?.[cat] || [];
  const active = CATS.find((c) => c.key === cat);

  return (
    <div className="rounded-xl border border-border bg-card p-5 sm:p-6" data-testid="actus-panel">
      <div className="flex items-center justify-between gap-2 mb-4 flex-wrap">
        <h2 className="text-lg font-bold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
          <Newspaper className="h-5 w-5 text-accent" /> Actualités & veille réglementaire
        </h2>
        {compact && (
          <Link to="/actualites" className="text-xs text-accent hover:underline flex items-center gap-1" data-testid="actus-voir-tout">
            Tout voir <ArrowRight className="h-3 w-3" />
          </Link>
        )}
      </div>

      {annonces.length > 0 && (
        <div className="space-y-2 mb-4">
          {annonces.map((a) => (
            <div key={a.id} className="rounded-lg border border-primary/30 bg-primary/5 px-3 py-2.5" data-testid={`annonce-${a.id}`}>
              <p className="text-sm font-semibold flex items-center gap-1.5 flex-wrap">
                <Pin className="h-3.5 w-3.5 text-accent shrink-0" /> {a.titre}
                {a.lien && (
                  <a href={a.lien} target="_blank" rel="noopener noreferrer" className="text-accent hover:underline" data-testid={`annonce-lien-${a.id}`}>
                    <ExternalLink className="h-3.5 w-3.5 inline" />
                  </a>
                )}
              </p>
              {a.contenu && <p className="text-xs text-muted-foreground mt-0.5 whitespace-pre-wrap">{a.contenu}</p>}
              <p className="text-[10px] font-mono text-muted-foreground mt-1">Publié le {a.created_at?.slice(0, 10)} par {a.par}</p>
            </div>
          ))}
        </div>
      )}

      <div className="flex gap-1.5 mb-4 flex-wrap" role="tablist">
        {CATS.map((c) => (
          <button key={c.key} role="tab" aria-selected={cat === c.key} onClick={() => setCat(c.key)} data-testid={`actus-tab-${c.key}`}
            className={`flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${cat === c.key ? c.badge : "border-border text-muted-foreground hover:text-foreground"}`}>
            <span className={`h-2 w-2 rounded-full ${c.dot}`} /> {c.label}
          </button>
        ))}
      </div>

      <div className={`grid grid-cols-1 ${compact ? "" : "lg:grid-cols-2"} gap-3`}>
        {actus.map((n, i) => (
          <a key={n.url} href={n.url} target="_blank" rel="noopener noreferrer" data-testid={`actu-${cat}-${i}`}
            className="group relative flex rounded-xl border border-border bg-background/50 overflow-hidden hover:border-accent/60 hover:shadow-lg hover:shadow-black/20 transition-all">
            <span className={`w-1 shrink-0 ${active.dot}`} />
            <div className="p-4 min-w-0">
              <div className="flex items-center gap-2 mb-1.5">
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-[10px] font-extrabold text-white ${active.dot}`}>{n.source[0]}</span>
                <span className="text-xs font-semibold truncate">{n.source}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded-full border border-border text-muted-foreground shrink-0">Officiel</span>
              </div>
              <p className="text-sm font-bold leading-snug group-hover:text-accent transition-colors">{n.titre}</p>
              <p className="text-xs text-muted-foreground mt-1 leading-relaxed">{n.resume}</p>
              <p className="text-[10px] font-mono text-muted-foreground mt-2 flex items-center gap-1 group-hover:text-accent transition-colors">
                Lire sur le site officiel <ExternalLink className="h-3 w-3" />
              </p>
            </div>
          </a>
        ))}
      </div>

      {liens.length > 0 && (
        <div className="mt-4 pt-3 border-t border-border">
          <p className="text-xs font-semibold text-muted-foreground flex items-center gap-1.5 mb-2">
            <Link2 className="h-3.5 w-3.5" /> Liens utiles
          </p>
          <div className="flex flex-wrap gap-1.5">
            {liens.map((l, i) => (
              <a key={l.url} href={l.url} target="_blank" rel="noopener noreferrer" data-testid={`lien-${cat}-${i}`}
                className="rounded-full border border-border px-2.5 py-1 text-[11px] text-muted-foreground hover:text-foreground hover:border-accent/50 transition-colors">
                {l.titre}
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
